/**
 * 旅行回忆系统 - API服务
 * 注意：travel-memories服务器运行在3001端口
 */

import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

// 创建专门的axios实例用于travel-memories API
// 开发环境使用3001端口，生产环境使用相对路径
const getTravelMemoriesApiUrl = () => {
  // 开发环境：始终使用 localhost:3001
  // 这样可以避免hostname判断失败导致的网络错误
  if (process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) {
    return 'http://localhost:3001';
  }
  
  // 生产环境：使用相对路径（假设travel-memories服务器和前端在同一域名下）
  return '';
};

// 创建travel-memories专用的axios实例
const travelMemoriesApi = axios.create({
  baseURL: getTravelMemoriesApiUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器，添加认证令牌
travelMemoriesApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 调试日志
    console.log('【TravelMemoriesAPI】请求:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasToken: !!token
    });
    
    return config;
  },
  (error) => {
    console.error('【TravelMemoriesAPI】请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器，处理常见错误
travelMemoriesApi.interceptors.response.use(
  (response) => {
    // 直接返回 response.data，与其他API保持一致
    // 后端返回格式: { success: true, data: [...] }
    return response.data;
  },
  (error) => {
    // 处理401错误（未认证）
    if (error.response && error.response.status === 401) {
      console.warn('Travel Memories API 401错误：清除token并重定向到登录页', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        message: error.response?.data?.message
      });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    // 处理403错误（权限不足）- 不要跳转到登录页，只记录错误
    if (error.response && error.response.status === 403) {
      console.warn('Travel Memories API 403错误：权限不足', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        message: error.response?.data?.message
      });
      // 403错误不跳转到登录页，让组件自己处理
    }
    return Promise.reject(error);
  }
);

const BASE_URL = '/api/travel-memories';

// 旅行详情缓存：复用同一批签名 URL，避免每次进详情都重新请求导致图片重复加载
const TRAVEL_DETAIL_CACHE_TTL_MS = 100 * 60 * 1000; // 100 分钟（签名 2h，留余量）
const travelDetailCache = new Map();

function getTravelDetailCacheKey(spaceId, travelId) {
  return `${spaceId}/${travelId}`;
}

function getCachedTravelDetail(spaceId, travelId) {
  const key = getTravelDetailCacheKey(spaceId, travelId);
  const entry = travelDetailCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > TRAVEL_DETAIL_CACHE_TTL_MS) {
    travelDetailCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCachedTravelDetail(spaceId, travelId, data) {
  const key = getTravelDetailCacheKey(spaceId, travelId);
  travelDetailCache.set(key, { data, ts: Date.now() });
}

// 空间管理API
export const spaceApi = {
  // 创建空间
  createSpace: (data) => {
    return travelMemoriesApi.post(`${BASE_URL}/spaces`, data);
  },

  // 获取用户的所有空间
  getSpaces: () => {
    return travelMemoriesApi.get(`${BASE_URL}/spaces`);
  },

  // 获取空间详情
  getSpaceById: (id) => {
    return travelMemoriesApi.get(`${BASE_URL}/spaces/${id}`);
  },

  // 更新空间
  updateSpace: (id, data) => {
    return travelMemoriesApi.put(`${BASE_URL}/spaces/${id}`, data);
  },

  // 删除空间
  deleteSpace: (id) => {
    return travelMemoriesApi.delete(`${BASE_URL}/spaces/${id}`);
  }
};

// 空间成员管理API
export const spaceMemberApi = {
  // 获取成员列表
  getMembers: (spaceId) => {
    return travelMemoriesApi.get(`${BASE_URL}/spaces/${spaceId}/members`);
  },

  // 添加成员
  addMember: (spaceId, data) => {
    return travelMemoriesApi.post(`${BASE_URL}/spaces/${spaceId}/members`, data);
  },

  // 更新成员角色
  updateMemberRole: (spaceId, userId, role) => {
    return travelMemoriesApi.put(`${BASE_URL}/spaces/${spaceId}/members/${userId}`, { role });
  },

  // 移除成员
  removeMember: (spaceId, userId) => {
    return travelMemoriesApi.delete(`${BASE_URL}/spaces/${spaceId}/members/${userId}`);
  }
};

// 旅行管理API
export const travelApi = {
  // 获取空间的所有旅行
  getTravels: (spaceId) => {
    return travelMemoriesApi.get(`${BASE_URL}/spaces/${spaceId}/travels`);
  },

  // 获取旅行详情（带缓存：同一批签名 URL 复用，浏览器可缓存图片/视频）
  getTravelById: async (spaceId, travelId) => {
    const cached = getCachedTravelDetail(spaceId, travelId);
    if (cached) return cached;
    const response = await travelMemoriesApi.get(
      `${BASE_URL}/spaces/${spaceId}/travels/${travelId}`
    );
    if (response && response.success && response.data) {
      setCachedTravelDetail(spaceId, travelId, response);
    }
    return response;
  },

  // 创建旅行
  createTravel: (spaceId, data) => {
    return travelMemoriesApi.post(`${BASE_URL}/spaces/${spaceId}/travels`, data);
  },

  // 更新旅行（会清除该条详情缓存，下次进入拉新数据）
  updateTravel: async (spaceId, travelId, data) => {
    const res = await travelMemoriesApi.put(
      `${BASE_URL}/spaces/${spaceId}/travels/${travelId}`,
      data
    );
    travelDetailCache.delete(getTravelDetailCacheKey(spaceId, travelId));
    return res;
  },

  // 删除旅行
  deleteTravel: async (spaceId, travelId) => {
    const res = await travelMemoriesApi.delete(
      `${BASE_URL}/spaces/${spaceId}/travels/${travelId}`
    );
    travelDetailCache.delete(getTravelDetailCacheKey(spaceId, travelId));
    return res;
  },

  // 生成分享链接
  generateShareLink: async (spaceId, travelId, expiresIn) => {
    return travelMemoriesApi.post(
      `${BASE_URL}/spaces/${spaceId}/travels/${travelId}/share-link`,
      { expiresIn }
    );
  },

  // 获取旅行详情（公开分享，需要token）
  getTravelByIdPublic: async (travelId, token) => {
    // 使用普通的axios实例，不添加token
    const plainAxios = axios.create({
      baseURL: getApiBaseUrl(),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    try {
      const response = await plainAxios.get(`/api/travel-memories/share/travels/${travelId}`, {
        params: { token }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// 图片上传API
export const uploadApi = {
  // 上传图片
  uploadImage: (spaceId, formData) => {
    return travelMemoriesApi.post(`${BASE_URL}/spaces/${spaceId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // 删除图片
  deleteImage: (spaceId, photoId, filePath) => {
    return travelMemoriesApi.delete(`${BASE_URL}/spaces/${spaceId}/photos/${photoId}`, {
      data: { path: filePath }
    });
  }
};

export default {
  spaceApi,
  spaceMemberApi,
  travelApi,
  uploadApi
};
